import React from "react";
import { Badge } from "react-bootstrap";
import { stateCodeForPincode } from "../../utils/PincodeStateMapper";
import { getStateName } from "../../utils/GstStates";

/**
 * Predicts, on this device, how the server will file the e-Way bill for this sale
 * BEFORE it syncs — the actual bill is generated server-side, so the outpost can't
 * call the preview API and instead mirrors the server's rules locally
 * (see EwayBillPayloadBuilder.java + PincodeStateMapper.java).
 *
 * Rule (kept in step with the server):
 *   - billToState  = first 2 digits of the customer's GSTIN (unknown if no GSTIN)
 *   - shipToState  = state derived from the selected ship-to address's pincode
 *   - no ship-to selected (ships to billing address)            -> Regular (1)
 *   - ship-to pincode couldn't be matched to a state            -> Regular (1) + note
 *   - ship-to state != bill-to state (both known)               -> Bill To / Ship To (2), ship-to GSTIN = URP
 *   - otherwise                                                 -> Regular (1)
 *
 * Props:
 *   gstNumber      - the customer's GST number (string)
 *   selectedShipTo - the resolved shipping address object ({ address, pinCode }) or null
 *   hasShipTo      - whether the operator picked a shipping address (vs. billing address)
 */
export function EwayDecisionsPreview({ gstNumber, selectedShipTo, hasShipTo }) {
    const gstin = String(gstNumber || "").trim().toUpperCase();
    // Bill-to state = first 2 digits of the customer's GSTIN. Unknown when there's
    // no GSTIN (or it's too short) — in that case we don't predict an interstate move.
    const billToState = gstin.length >= 2 && /^[0-9]{2}/.test(gstin)
        ? parseInt(gstin.substring(0, 2), 10)
        : 0;

    const shipPin = hasShipTo ? String(selectedShipTo?.pinCode || "").trim() : "";
    const shipToState = hasShipTo ? stateCodeForPincode(shipPin) : 0;

    // Decide the transaction type exactly as the server does.
    let isBillToShipTo = false;
    let pincodeUnresolved = false;
    if (hasShipTo) {
        if (shipToState === 0) {
            // Couldn't derive a state from the pincode — server treats this as same-state.
            pincodeUnresolved = true;
        } else if (billToState > 0 && shipToState !== billToState) {
            isBillToShipTo = true;
        }
    }

    const shipStateName = getStateName(shipToState) || `state ${shipToState}`;
    const billStateName = getStateName(billToState) || `state ${billToState}`;

    // Card language matches the purple e-Way accent used by the Ship To picker.
    const purple = "#635BFF";
    return (
        <div className="mt-2 px-3 py-2" style={{ borderRadius: "8px", border: "1px solid #d6d3ff", backgroundColor: "#f6f5ff" }}>
            <div className="d-flex align-items-center justify-content-between mb-1">
                <span className="d-inline-flex align-items-center">
                    <i className="bi bi-diagram-3 me-2" style={{ color: purple, fontSize: "0.85rem" }}></i>
                    <span className="fw-semibold" style={{ color: purple, fontSize: "0.72rem", letterSpacing: "0.4px" }}>
                        HOW THIS E-WAY BILL WILL BE FILED
                    </span>
                </span>
                {isBillToShipTo ? (
                    <Badge bg="" style={{ backgroundColor: purple, color: "#fff" }}>Bill To / Ship To</Badge>
                ) : (
                    <Badge bg="" style={{ backgroundColor: "#e8fff3", color: "#0f9d58", border: "1px solid #b6e6cc" }}>Regular</Badge>
                )}
            </div>

            {isBillToShipTo ? (
                <div className="small text-gray-700" style={{ lineHeight: 1.5 }}>
                    <div>
                        The delivery is in <span className="fw-semibold">{shipStateName}</span>, a different state from the
                        customer's registered state (<span className="fw-semibold">{billStateName}</span>) — so the bill is raised
                        as <span className="fw-semibold">Bill To / Ship To</span>, keeping your tax matched to the invoice while
                        the interstate movement of goods is recorded.
                    </div>
                    <div className="mt-1 text-muted" style={{ fontSize: "0.72rem" }}>
                        <i className="bi bi-shield-lock me-1"></i>
                        The ship-to GSTIN will be sent as <span className="fw-semibold">URP</span> (Unregistered Person), because
                        the delivery site isn't separately GST-registered under the customer's GSTIN.
                    </div>
                </div>
            ) : (
                <div className="small text-gray-700" style={{ lineHeight: 1.5 }}>
                    {!hasShipTo ? (
                        <span>Delivered to the customer's billing address, so it's filed as an ordinary Regular bill.</span>
                    ) : pincodeUnresolved ? (
                        <span>Filed as an ordinary Regular bill for now — see below.</span>
                    ) : billToState === 0 ? (
                        <span>Filed as an ordinary Regular bill.</span>
                    ) : (
                        <span>
                            The delivery is in <span className="fw-semibold">{shipStateName}</span> — the customer's own state — so
                            it's filed as a normal Regular bill.
                        </span>
                    )}

                    {pincodeUnresolved && (
                        <div className="mt-1" style={{ color: "#b58f00", fontSize: "0.72rem" }}>
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            We couldn't work out the delivery state from the pincode
                            {shipPin ? <> (<span className="fw-semibold">{shipPin}</span>)</> : null}. If this address is in another
                            state, check the pincode so it can be filed as Bill To / Ship To.
                        </div>
                    )}

                    {hasShipTo && !pincodeUnresolved && billToState === 0 && (
                        <div className="mt-1 text-muted" style={{ fontSize: "0.72rem" }}>
                            <i className="bi bi-info-circle me-1"></i>
                            The customer has no GSTIN, so a cross-state delivery can't be worked out here — the server has the final say.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
